import {Component, inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {MessageService} from 'primeng/api';
import {Card} from 'primeng/card';
import {Skeleton} from 'primeng/skeleton';
import {Toast} from 'primeng/toast';
import {AdminListasService, EnumItem} from '@core/services/admin-listas.service';

export interface TipoLista {
  tipo: string;
  label: string;
  icon: string;
  isConcessionaria?: boolean;
}

@Component({
  selector: 'app-admin-listas',
  imports: [Card, FormsModule, RouterLink, Skeleton, Toast],
  providers: [MessageService],
  templateUrl: './admin-listas.html',
})
export class AdminListas implements OnInit {
  protected readonly tipos: TipoLista[] = [
    {tipo: 'fase', label: 'Fases', icon: 'pi-list-check'},
    {tipo: 'status', label: 'Status', icon: 'pi-tag'},
    {tipo: 'motivo', label: 'Motivos', icon: 'pi-question-circle'},
    {tipo: 'curso', label: 'Cursos', icon: 'pi-book'},
    {tipo: 'grupo', label: 'Grupos', icon: 'pi-sitemap'},
    {tipo: 'nivel', label: 'Níveis', icon: 'pi-chart-bar'},
    {tipo: 'tipo-tap', label: 'Tipos de TAP', icon: 'pi-file'},
    {tipo: 'situacao-tap', label: 'Situações de TAP', icon: 'pi-check-circle'},
    {tipo: 'diretoria', label: 'Diretorias', icon: 'pi-building'},
    {tipo: 'superintendencia', label: 'Superintendências', icon: 'pi-briefcase'},
    {tipo: 'concessionaria', label: 'Concessionárias', icon: 'pi-car', isConcessionaria: true},
  ];

  protected items: Record<string, EnumItem[]> = {};
  protected loadingMap: Record<string, boolean> = {};
  protected novoNome: Record<string, string> = {};
  protected novoLote: Record<string, string> = {};
  protected savingMap: Record<string, boolean> = {};
  protected confirmando: {tipo: string; id: number} | null = null;

  private service = inject(AdminListasService);
  private messageService = inject(MessageService);

  ngOnInit() {
    this.tipos.forEach(t => {
      this.items[t.tipo] = [];
      this.novoNome[t.tipo] = '';
      this.novoLote[t.tipo] = '';
      this.loadingMap[t.tipo] = true;
      this.savingMap[t.tipo] = false;
      this.service.listar(t.tipo).subscribe({
        next: data => {
          this.items[t.tipo] = data;
          this.loadingMap[t.tipo] = false;
        },
        error: () => {
          this.loadingMap[t.tipo] = false;
        }
      });
    });
  }

  protected criar(tipo: TipoLista) {
    if (tipo.isConcessionaria) {
      this.criarConcessionaria(tipo.tipo);
    } else {
      this.criarSimples(tipo.tipo);
    }
  }

  private criarSimples(tipo: string) {
    const nome = this.novoNome[tipo]?.trim();
    if (!nome || this.savingMap[tipo]) return;
    this.savingMap[tipo] = true;
    this.service.criar(tipo, nome).subscribe({
      next: item => {
        this.items[tipo] = [...this.items[tipo], item]
          .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', {sensitivity: 'base'}));
        this.novoNome[tipo] = '';
        this.savingMap[tipo] = false;
        this.messageService.add({severity: 'success', summary: 'Criado', detail: `"${item.nome}" adicionado com sucesso.`});
      },
      error: err => {
        this.savingMap[tipo] = false;
        const msg = err?.error?.message ?? 'Erro ao criar item.';
        this.messageService.add({severity: 'error', summary: 'Erro', detail: msg, life: 6000});
      }
    });
  }

  private criarConcessionaria(tipo: string) {
    const lote = this.novoLote[tipo]?.trim();
    const nome = this.novoNome[tipo]?.trim();
    if (!lote || !nome || this.savingMap[tipo]) return;
    this.savingMap[tipo] = true;
    this.service.criarConcessionaria(lote, nome).subscribe({
      next: item => {
        this.items[tipo] = [...this.items[tipo], item]
          .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', {sensitivity: 'base'}));
        this.novoNome[tipo] = '';
        this.novoLote[tipo] = '';
        this.savingMap[tipo] = false;
        this.messageService.add({severity: 'success', summary: 'Criada', detail: `"${item.nome}" adicionada com sucesso.`});
      },
      error: err => {
        this.savingMap[tipo] = false;
        const msg = err?.error?.message ?? 'Erro ao criar concessionária.';
        this.messageService.add({severity: 'error', summary: 'Erro', detail: msg, life: 6000});
      }
    });
  }

  protected podeAdicionar(tipo: TipoLista): boolean {
    if (tipo.isConcessionaria) {
      return !!(this.novoLote[tipo.tipo]?.trim()) && !!(this.novoNome[tipo.tipo]?.trim()) && !this.savingMap[tipo.tipo];
    }
    return !!(this.novoNome[tipo.tipo]?.trim()) && !this.savingMap[tipo.tipo];
  }

  protected solicitarExclusao(tipo: string, id: number) {
    this.confirmando = {tipo, id};
  }

  protected cancelarExclusao() {
    this.confirmando = null;
  }

  protected confirmarExclusao() {
    if (!this.confirmando) return;
    const {tipo, id} = this.confirmando;
    this.confirmando = null;
    this.service.excluir(tipo, id).subscribe({
      next: () => {
        this.items[tipo] = this.items[tipo].filter(i => i.id !== id);
        this.messageService.add({severity: 'success', summary: 'Excluído', detail: 'Item removido com sucesso.'});
      },
      error: err => {
        const msg = err?.error?.message ?? 'Erro ao excluir item.';
        this.messageService.add({severity: 'error', summary: 'Não foi possível excluir', detail: msg, life: 8000});
      }
    });
  }

  protected isConfirmando(tipo: string, id: number): boolean {
    return this.confirmando?.tipo === tipo && this.confirmando?.id === id;
  }
}
